import * as THREE from 'three';
import { defaultControlSettings, normalizeControlSettings, type ControlAction, type ControlSettings, type PlayerControlSettings } from './ControlSettings';
import { assignGamepads, padButton, readGamepads } from './Gamepads';

export interface ControlIntent {
  moveX: number;
  moveZ: number;
  lookX?: number;
  lookZ?: number;
  sprint: boolean;
  shootHeld: boolean;
  shootPressed: boolean;
  shootReleased: boolean;
  passPressed: boolean;
  punchPressed: boolean;
  active: boolean;
}

interface RawState {
  moveX: number;
  moveZ: number;
  sprint: boolean;
  shoot: boolean;
  pass: boolean;
  punch: boolean;
  passPressed: boolean;
  punchPressed: boolean;
  active: boolean;
}

interface TouchControlElements {
  root: HTMLElement;
  stick: HTMLElement;
  knob: HTMLElement;
  buttons: Record<'pass' | 'shoot' | 'punch' | 'sprint', HTMLElement>;
}

interface TouchState extends TouchControlElements {
  vector: THREE.Vector2;
  active: boolean;
  sprint: boolean;
  shoot: boolean;
  pass: boolean;
  punch: boolean;
  pending: Set<'pass' | 'shoot' | 'punch' | 'sprint'>;
  pointerId: number | null;
  centerX: number;
  centerY: number;
  radius: number;
}

function emptyRaw(): RawState {
  return { moveX: 0, moveZ: 0, sprint: false, shoot: false, pass: false, punch: false, passPressed: false, punchPressed: false, active: false };
}

function normalizeMove(raw: RawState): void {
  const len = Math.hypot(raw.moveX, raw.moveZ);
  if (len > 1) {
    raw.moveX /= len;
    raw.moveZ /= len;
  }
}

export class InputController {
  readonly menu = { x: 0, y: 0, accept: false, back: false, start: false };
  gamepadDisconnected = false;
  private assignedPads = [-1, -1];
  private connectedPads = [false, false];
  private blockedGamepadActions = [false, false];
  private menuHeld = { accept: false, back: false, start: false, x: 0, y: 0 };
  private menuRepeatAt = 0;
  private readonly keys = new Set<string>();
  private readonly pressedThisFrame = new Set<string>();
  private readonly pendingKeys = new Set<string>();
  private readonly touches: TouchState[] = [];
  private localPlayerCount = 2;
  private settings: ControlSettings = defaultControlSettings();
  private readonly joined = [false, false];
  private readonly gamepadStartPressed = [false, false];
  private readonly previousGamepadStart = [false, false];
  private readonly intents: ControlIntent[] = [
    { moveX: 0, moveZ: 0, sprint: false, shootHeld: false, shootPressed: false, shootReleased: false, passPressed: false, punchPressed: false, active: false },
    { moveX: 0, moveZ: 0, sprint: false, shootHeld: false, shootPressed: false, shootReleased: false, passPressed: false, punchPressed: false, active: false },
  ];
  private previousPass = [false, false];
  private previousPunch = [false, false];

  private readonly onKeyDown = (event: KeyboardEvent) => {
    if (event.target instanceof HTMLElement && (event.target.isContentEditable || event.target.closest('input, textarea, select'))) return;
    if (event.code === 'Space' && event.target instanceof HTMLElement && event.target.closest('#match-controls button, dialog button, .screen button')) return;
    if (this.settings.some(settings => Object.values(settings.keys).includes(event.code))) event.preventDefault();
    if (!this.keys.has(event.code)) {
      this.pressedThisFrame.add(event.code);
      this.pendingKeys.add(event.code);
    }
    this.keys.add(event.code);
  };

  private readonly onKeyUp = (event: KeyboardEvent) => {
    this.keys.delete(event.code);
  };

  private readonly onBlur = () => {
    this.keys.clear();
    this.pressedThisFrame.clear();
    this.pendingKeys.clear();
    this.touches.forEach((touch, player) => {
      touch.pending.clear();
      touch.active = false;
      touch.vector.set(0, 0);
      touch.sprint = false;
      touch.shoot = false;
      touch.pass = false;
      touch.punch = false;
      touch.pointerId = null;
      this.updateKnob(player);
      for (const button of Object.values(touch.buttons)) button.classList.remove('pressed');
    });
    for (const intent of this.intents) {
      intent.moveX = 0;
      intent.moveZ = 0;
      intent.sprint = false;
      intent.shootHeld = false;
      intent.shootPressed = false;
      intent.shootReleased = false;
      intent.passPressed = false;
      intent.punchPressed = false;
      intent.active = false;
    }
    this.previousPass.fill(false);
    this.previousPunch.fill(false);
    this.gamepadStartPressed.fill(false);
    this.previousGamepadStart.fill(false);
  };

  constructor(
    stick: HTMLElement,
    knob: HTMLElement,
    touchButtons: Record<'pass' | 'shoot' | 'punch' | 'sprint', HTMLElement>,
    touchPlayer2?: TouchControlElements,
  ) {
    this.touches.push({ root: stick.closest<HTMLElement>('.touch-player-controls') ?? stick, stick, knob, buttons: touchButtons, vector: new THREE.Vector2(), active: false, sprint: false, shoot: false, pass: false, punch: false, pending: new Set(), pointerId: null, centerX: 0, centerY: 0, radius: 1 });
    if (touchPlayer2) this.touches.push({ ...touchPlayer2, vector: new THREE.Vector2(), active: false, sprint: false, shoot: false, pass: false, punch: false, pending: new Set(), pointerId: null, centerX: 0, centerY: 0, radius: 1 });
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    this.touches.forEach((touch, player) => this.bindTouchControls(touch, player));
  }

  setControlSettings(settings: unknown): void {
    this.settings = normalizeControlSettings(settings);
    this.assignedPads = [-1, -1];
    this.connectedPads.fill(false);
    this.setLocalPlayerCount(this.localPlayerCount);
  }

  setLocalPlayerCount(count: number): void {
    this.localPlayerCount = count;
    this.touches.forEach((touch, player) => touch.root.classList.toggle('hidden', player >= count || this.settings[player].mode !== 'touch'));
    document.body.classList.toggle('touch-mode', this.settings.some((player, slot) => slot < count && player.mode === 'touch'));
    this.clear();
  }

  update(): void {
    const raws = [emptyRaw(), emptyRaw()];
    this.gamepadStartPressed.fill(false);
    this.readKeyboard(raws[0], this.settings[0]);
    this.readKeyboard(raws[1], this.settings[1]);
    this.mergeTouch(raws[0], 0);
    this.mergeTouch(raws[1], 1);
    this.readGamepads(raws);

    for (let i = 0; i < 2; i += 1) {
      const raw = raws[i];
      normalizeMove(raw);
      const intent = this.intents[i];
      const prevShoot = intent.shootHeld;
      const prevPass = this.previousPass[i];
      const prevPunch = this.previousPunch[i];
      intent.moveX = raw.moveX;
      intent.moveZ = raw.moveZ;
      intent.sprint = raw.sprint;
      intent.shootHeld = raw.shoot;
      intent.shootPressed = raw.shoot && !prevShoot;
      intent.shootReleased = prevShoot && !raw.shoot;
      intent.passPressed = raw.passPressed || (raw.pass && !prevPass);
      intent.punchPressed = raw.punchPressed || (raw.punch && !prevPunch);
      intent.active = raw.active;
      this.joined[i] ||= raw.active;
      this.previousPass[i] = raw.pass;
      this.previousPunch[i] = raw.punch;
    }
    this.pendingKeys.clear();
    this.touches.forEach(touch => touch.pending.clear());
  }

  getIntent(player: 0 | 1): ControlIntent {
    return this.intents[player];
  }

  getPlayerConnected(player: 0 | 1): boolean {
    return this.joined[player];
  }

  wasGamepadStartPressed(player: 0 | 1): boolean {
    return this.gamepadStartPressed[player];
  }

  wasPressed(code: string): boolean {
    return this.pressedThisFrame.has(code);
  }

  endFrame(): void {
    this.pressedThisFrame.clear();
  }

  private readKeyboard(raw: RawState, settings: PlayerControlSettings): void {
    if (settings.mode !== 'keyboard') return;
    const has = (action: ControlAction) => this.keys.has(settings.keys[action]);
    const pressed = (action: ControlAction) => this.pendingKeys.has(settings.keys[action]);
    const moveX = Number(has('right')) - Number(has('left'));
    const moveZ = Number(has('down')) - Number(has('up'));
    raw.moveX += moveX;
    raw.moveZ += moveZ;
    raw.sprint ||= has('sprint');
    raw.shoot ||= has('shoot') || pressed('shoot');
    raw.pass ||= has('pass');
    raw.punch ||= has('punch');
    raw.passPressed ||= pressed('pass');
    raw.punchPressed ||= pressed('punch');
    if (moveX !== 0 || moveZ !== 0 || raw.sprint || raw.shoot || raw.pass || raw.punch || raw.passPressed || raw.punchPressed) {
      raw.active = true;
    }
  }

  private mergeTouch(raw: RawState, player: 0 | 1): void {
    const touch = this.touches[player];
    if (this.settings[player].mode !== 'touch' || !touch) return;
    raw.moveX += touch.vector.x;
    raw.moveZ += touch.vector.y;
    raw.sprint ||= touch.sprint;
    raw.shoot ||= touch.shoot || touch.pending.has('shoot');
    raw.pass ||= touch.pass;
    raw.punch ||= touch.punch;
    raw.passPressed ||= touch.pending.has('pass');
    raw.punchPressed ||= touch.pending.has('punch');
    if (touch.active || raw.shoot || raw.pass || raw.punch || raw.sprint || raw.passPressed || raw.punchPressed) {
      raw.active = true;
    }
  }

  private readGamepads(raws: RawState[]): void {
    const pads = readGamepads();
    this.gamepadDisconnected = this.assignedPads.some((index, player) => player < this.localPlayerCount && this.connectedPads[player] && !pads[index]?.connected);
    this.assignedPads = assignGamepads(this.settings, pads, this.assignedPads);
    this.connectedPads = this.assignedPads.map(index => pads[index]?.connected === true);
    this.readGamepadMenu(pads);
    for (let player = 0; player < 2; player += 1) {
      const settings = this.settings[player];
      const pad = pads[this.assignedPads[player]];
      if (!pad?.connected) {
        this.previousGamepadStart[player] = false;
        continue;
      }
      const start = padButton(pad, settings.buttons.pause);
      this.gamepadStartPressed[player] = start && !this.previousGamepadStart[player];
      this.previousGamepadStart[player] = start;
      if (this.settings[player].mode !== 'gamepad') continue;
      if (this.blockedGamepadActions[player]) {
        if ((['pass', 'shoot', 'punch', 'sprint'] as const).some(action => padButton(pad, settings.buttons[action]))) continue;
        this.blockedGamepadActions[player] = false;
      }
      const raw = raws[player];
      const axis = (index: number, invert: boolean) => {
        const value = pad.axes[index] ?? 0;
        return Number.isFinite(value) && Math.abs(value) > settings.deadzone ? Math.max(-1, Math.min(1, value)) * (invert ? -1 : 1) : 0;
      };
      const ax = axis(settings.axisX, settings.invertX);
      const ay = axis(settings.axisY, settings.invertY);
      const dpadX = Number(padButton(pad, settings.buttons.right)) - Number(padButton(pad, settings.buttons.left));
      const dpadY = Number(padButton(pad, settings.buttons.down)) - Number(padButton(pad, settings.buttons.up));
      raw.moveX += ax !== 0 ? ax : dpadX;
      raw.moveZ += ay !== 0 ? ay : dpadY;
      raw.pass ||= padButton(pad, settings.buttons.pass);
      raw.shoot ||= padButton(pad, settings.buttons.shoot);
      raw.punch ||= padButton(pad, settings.buttons.punch);
      raw.sprint ||= padButton(pad, settings.buttons.sprint);
      if (ax !== 0 || ay !== 0 || dpadX !== 0 || dpadY !== 0 || raw.pass || raw.shoot || raw.punch || raw.sprint) raw.active = true;
    }
  }

  private readGamepadMenu(pads: readonly (Gamepad | null)[]): void {
    let x = 0, y = 0, accept = false, back = false, start = false;
    for (let index = 0; index < pads.length; index += 1) {
      const pad = pads[index];
      if (!pad?.connected) continue;
      const player = this.assignedPads.indexOf(index);
      const settings = this.settings[player >= 0 ? player : 0];
      const axisX = (pad.axes[settings.axisX] ?? 0) * (settings.invertX ? -1 : 1);
      const axisY = (pad.axes[settings.axisY] ?? 0) * (settings.invertY ? -1 : 1);
      x ||= Number(padButton(pad, settings.buttons.right)) - Number(padButton(pad, settings.buttons.left)) || (Math.abs(axisX) > 0.55 ? Math.sign(axisX) : 0);
      y ||= Number(padButton(pad, settings.buttons.down)) - Number(padButton(pad, settings.buttons.up)) || (Math.abs(axisY) > 0.55 ? Math.sign(axisY) : 0);
      accept ||= padButton(pad, 0);
      back ||= padButton(pad, 1) || padButton(pad, 8);
      start ||= padButton(pad, settings.buttons.pause);
    }
    this.menu.accept = accept && !this.menuHeld.accept;
    this.menu.back = back && !this.menuHeld.back;
    this.menu.start = start && !this.menuHeld.start;
    const changed = x !== this.menuHeld.x || y !== this.menuHeld.y;
    const now = performance.now();
    const repeat = changed || now >= this.menuRepeatAt;
    this.menu.x = repeat ? x : 0;
    this.menu.y = repeat ? y : 0;
    if (repeat) this.menuRepeatAt = now + (changed ? 360 : 130);
    Object.assign(this.menuHeld, { x, y, accept, back, start });
  }

  private bindTouchControls(touch: TouchState, player: number): void {
    touch.stick.addEventListener('pointerdown', event => this.onStickDown(event, player));
    touch.stick.addEventListener('pointermove', event => this.onStickMove(event, player));
    touch.stick.addEventListener('pointerup', event => this.onStickUp(event, player));
    touch.stick.addEventListener('pointercancel', event => this.onStickUp(event, player));
    touch.stick.addEventListener('lostpointercapture', event => this.onStickUp(event, player));
    for (const action of ['pass', 'shoot', 'punch', 'sprint'] as const) {
      const button = touch.buttons[action];
      button.addEventListener('pointerdown', event => {
        event.preventDefault();
        button.classList.add('pressed');
        touch.pending.add(action);
        touch[action] = true;
      });
      const release = (event: Event) => {
        event.preventDefault();
        const cancelled = event.type === 'pointercancel' || (event.type === 'pointerleave' && button.classList.contains('pressed'));
        button.classList.remove('pressed');
        touch[action] = false;
        if (cancelled) {
          touch.pending.delete(action);
          if (action === 'shoot') this.intents[player].shootHeld = false;
        }
      };
      button.addEventListener('pointerup', release);
      button.addEventListener('pointercancel', release);
      button.addEventListener('pointerleave', release);
    }
  }

  private readonly onStickDown = (event: PointerEvent, player: number) => {
    event.preventDefault();
    const touch = this.touches[player];
    const rect = touch.stick.getBoundingClientRect();
    touch.pointerId = event.pointerId;
    touch.centerX = rect.left + rect.width / 2;
    touch.centerY = rect.top + rect.height / 2;
    touch.radius = rect.width * 0.42;
    touch.active = true;
    try {
      touch.stick.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic test events do not always have a capturable pointer id.
    }
    this.updatePointer(event.clientX, event.clientY, player);
  };

  private readonly onStickMove = (event: PointerEvent, player: number) => {
    const touch = this.touches[player];
    if (!touch.active || event.pointerId !== touch.pointerId) return;
    event.preventDefault();
    this.updatePointer(event.clientX, event.clientY, player);
  };

  private readonly onStickUp = (event: PointerEvent, player: number) => {
    const touch = this.touches[player];
    if (event.pointerId !== touch.pointerId) return;
    event.preventDefault();
    touch.pointerId = null;
    touch.active = false;
    touch.vector.set(0, 0);
    this.updateKnob(player);
  };

  private updatePointer(clientX: number, clientY: number, player: number): void {
    const touch = this.touches[player];
    const dx = clientX - touch.centerX;
    const dy = clientY - touch.centerY;
    touch.vector.set(dx / touch.radius, dy / touch.radius);
    if (touch.vector.lengthSq() > 1) touch.vector.normalize();
    this.updateKnob(player);
  }

  private updateKnob(player: number): void {
    const touch = this.touches[player];
    const distance = 38;
    touch.knob.style.transform = `translate(calc(-50% + ${touch.vector.x * distance}px), calc(-50% + ${touch.vector.y * distance}px))`;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
  }

  clear(): void {
    this.onBlur();
    const pads = readGamepads();
    const assigned = assignGamepads(this.settings, pads, this.assignedPads);
    this.blockedGamepadActions = assigned.map((index, player) => {
      const pad = pads[index];
      return !!pad?.connected && (['pass', 'shoot', 'punch', 'sprint'] as const).some(action => padButton(pad, this.settings[player].buttons[action]));
    });
  }
}
